import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, startWith, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { ButtonPosition, ModalButtons, NotificationTheme, TabLayoutType } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  GtmEvent,
  LoaderType,
  MediaQueryService,
  ScrollService,
  ToasterService,
} from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { Amount, BoundType, CheapestBoundPrice, LocationInfoMap, PaxAmount } from '@fcom/dapi';
import { finShare, safeMap, snapshot } from '@fcom/rx';
import { isPresent, LocalDate, mapValues, stopPropagation, unique, unsubscribe } from '@fcom/core/utils';
import {
  Bound,
  FareFamily,
  FareInformation,
  FinnairBoundItem,
  FinnairCabinClass,
  ItineraryItemFlight,
  ItineraryItemLayover,
  Offer,
  OfferList,
} from '@fcom/dapi/api/models';
import { Profile } from '@fcom/core-api/login';
import { profile } from '@fcom/core/selectors';
import { DimensionsAndDetails, SortBy } from '@fcom/common-booking';
import { globalBookingFlights, globalBookingPaxAmount } from '@fcom/common/store';
import { compareAsJson, GlobalBookingTravelClass } from '@fcom/core/index';
import { FAKE_LOAD_TIME } from '@fcom/common-booking/interfaces/flight-filters.interface';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import {
  AirOffersStatus,
  BookingAppState,
  BoundInfoWithFareFamilies,
  BoundInfoWithFareFamiliesMap,
  FareFamilyMap,
  QuotasPerBound,
  SelectionPhase,
  BoundWithLocations,
} from '@fcom/common/interfaces/booking';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { getCabinClassesForAwardBounds } from '@fcom/common-booking/utils/bound.utils';
import { BookingWidgetService } from '@fcom/booking-widget/services/booking-widget.service';

import { OffersActions } from '../../../store/actions';
import {
  boundSelectionsDone,
  currentFareFamilies,
  currentOffer,
  currentOffers,
  filteredCurrentOffersInbound,
  filteredCurrentOffersOutbound,
  flightSelectionGtmDataForOffers,
  hasZeroBaggageOnEitherFlight,
  isOneWayOffers,
  offersCurrency,
  offersDepartureDate,
  offersHash,
  offersReturnDate,
  offersStatus,
  quotasForBounds,
  selectedInboundFareFamilyCode,
  selectedInboundId,
  selectedOutboundFareFamilyCode,
  selectedOutboundId,
  selectedTicketSortBy,
} from '../../../store/selectors';
import { BookingQueueService, BookingService } from '../../../services';
import { CountryCode } from '../../../utils';
import { containsOnlyOneCabin, getBoundFareFamilies } from '../utils/offer.utils';
import { LocationNames, ItineraryDetails } from '../interfaces';

export const offerListSortMap: { [id: string]: string } = Object.freeze({
  [SortBy.DURATION]: 'default',
  [SortBy.PRICE]: 'price',
  [SortBy.ARRIVAL]: 'arrivalTime',
  [SortBy.DEPARTURE]: 'departureTime',
});

interface ItineraryModalState {
  open: boolean;
  data: BoundInfos;
}

interface ContinueModalState {
  open: boolean;
  data: ContinueModalData;
}

type BoundInfos = { outbound?: BoundInfoWithFareFamilies; inbound?: BoundInfoWithFareFamilies };

type ContinueModalData = {
  /**
   * Whether to show the zeroBaggage warning for the ticket type
   */
  zeroBaggage: boolean;
};

interface BenefitsModalState {
  open: boolean;
  data: DimensionsAndDetails;
}

const EMPTY_AMOUNT: Amount = { amount: undefined, currencyCode: 'EUR' };

const boundWithDetails = (offers: OfferList, bound: Bound): BoundWithLocations | undefined => {
  if (!bound) {
    return;
  }

  return {
    ...bound,
    departure: {
      ...bound.departure,
      cityName: offers.locations[bound.departure.locationCode]?.cityName,
      name: offers.locations[bound.departure.locationCode]?.name,
    },
    arrival: {
      ...bound.arrival,
      cityName: offers.locations[bound.arrival.locationCode]?.cityName,
      name: offers.locations[bound.arrival.locationCode]?.name,
    },
  };
};

const itineraryWithDetails = (
  boundFareInformation: FareInformation[],
  itinerary: (ItineraryItemFlight | ItineraryItemLayover)[],
  locations: LocationInfoMap
): ItineraryDetails[] => {
  return itinerary?.filter(isFlight).map((flight: ItineraryItemFlight) => {
    const matchingSegment = boundFareInformation?.find((segment) => segment.segmentId === flight?.id);

    return {
      ...flight,
      cabinClass: matchingSegment?.cabinClass,
      bookingClass: matchingSegment?.bookingClass,
      departure: {
        ...flight.departure,
        cityName: locations[flight.departure.locationCode]?.cityName,
      },
      arrival: {
        ...flight.arrival,
        cityName: locations[flight.arrival.locationCode]?.cityName,
      },
    };
  });
};

@Component({
  selector: 'fin-booking-ticket-selection-view',
  templateUrl: './booking-ticket-selection-view.component.html',
  styleUrls: ['./booking-ticket-selection-view.component.scss'],
  standalone: false,
})
export class BookingTicketSelectionViewComponent implements OnInit, OnDestroy {
  public readonly profile$: Observable<Profile>;

  public readonly AirOffersStatus = AirOffersStatus;
  public readonly BoundType = BoundType;
  public readonly SelectionPhase = SelectionPhase;
  public readonly ModalButtons = ModalButtons;
  public readonly LoaderType = LoaderType;
  public readonly ButtonPosition = ButtonPosition;
  public readonly TabLayoutType = TabLayoutType;

  public readonly offers$: Observable<OfferList>;
  public readonly outbounds$: Observable<BoundInfoWithFareFamiliesMap>;
  public readonly inbounds$: Observable<BoundInfoWithFareFamiliesMap>;
  public readonly currencyCode$: Observable<string>;
  public readonly offersStatus$: Observable<AirOffersStatus>;
  public readonly departureDate$: Observable<LocalDate>;
  public readonly returnDate$: Observable<LocalDate>;
  public readonly isOneWayOffers$: Observable<boolean>;
  public readonly fareFamilies$: Observable<FareFamilyMap>;
  public readonly selectedOutbound$: Observable<BoundInfoWithFareFamilies>;
  public readonly selectedInbound$: Observable<BoundInfoWithFareFamilies>;
  public readonly selectedOutboundFareFamilyCode$: Observable<string>;
  public readonly selectedInboundFareFamilyCode$: Observable<string>;
  public readonly selectedOutboundFareFamilyBenefits$: Observable<FareFamily>;
  public readonly selectedInboundFareFamilyBenefits$: Observable<FareFamily>;
  public readonly selectedOffer$: Observable<Offer>;
  public readonly selectedOutboundOfferItinerary$: Observable<ItineraryDetails[]>;
  public readonly selectedInboundOfferItinerary$: Observable<ItineraryDetails[]>;
  public readonly quotas$: Observable<QuotasPerBound>;
  public readonly paxAmount$: Observable<PaxAmount>;

  public readonly enabledOutboundIds$: Observable<string[]>;
  public readonly enabledInboundIds$: Observable<string[]>;

  public readonly cheapestCombinableOutboundPrices$: Observable<CheapestBoundPrice[]>;
  public readonly cheapestCombinableInboundPrices$: Observable<CheapestBoundPrice[]>;

  /**
   * Whether or not the bounds have been selected (both for two-way and the outbound for one-way trips.)
   */
  public readonly boundsSelected$: Observable<boolean>;
  public readonly selectableOffers$: Observable<Offer[]>;
  public readonly selectedOfferPrice$: Observable<Amount>;
  public readonly selectedOfferPoints$: Observable<number>;

  public readonly locations$: Observable<LocationInfoMap>;

  public readonly showKoreaNotification$: Observable<boolean>;
  public readonly isMobile$: Observable<boolean>;

  // == SELECTION PHASE ==
  public readonly selectionPhase$: BehaviorSubject<SelectionPhase> = new BehaviorSubject<SelectionPhase>(
    SelectionPhase.OUTBOUND
  );
  continueEnabled$: Observable<boolean>;
  continueButtonClicks$: Subject<boolean> = new Subject<boolean>();
  continueButtonId$: Observable<string>;

  // == SORTING ==
  public readonly selectedSortBy$: Observable<SortBy>;
  public readonly inboundIdSortOrder$: Observable<string[]>;
  public readonly outboundIdSortOrder$: Observable<string[]>;

  @Input()
  goToOutboundPhase$: Observable<void>;

  @Output()
  refetchOffers: EventEmitter<void> = new EventEmitter();

  @Output()
  selectionPhaseChange: EventEmitter<SelectionPhase> = new EventEmitter();

  itineraryModal: ItineraryModalState = {
    open: false,
    data: {},
  };

  continueModal: ContinueModalState = {
    open: false,
    data: null,
  };

  benefitsModal: BenefitsModalState = {
    open: false,
    data: null,
  };

  availableCabinClasses$: Observable<FinnairCabinClass[]>;

  private openBoundClick$ = new Subject<{ bound: BoundInfoWithFareFamilies; boundType: BoundType }>();
  private subscription = new Subscription();

  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  selectedSortCriteria$ = of(SortBy.PRICE);
  filteredOffersOutbound$: Observable<OfferList>;
  filteredOfferInbound$: Observable<OfferList>;
  outboundSorting$: Observable<{ [key: string]: string[] }>;
  inboundSorting$: Observable<{ [key: string]: string[] }>;
  showOutboundFilters$: Observable<boolean>;
  showInboundFilters$: Observable<boolean>;
  activeCabinClassNotificationIds: string[] = [];
  tripLocations$: Observable<LocationNames[]>;

  constructor(
    private bookingQueueService: BookingQueueService,
    private languageService: LanguageService,
    private store$: Store<BookingAppState>,
    private gtmService: GtmService,
    private bookingService: BookingService,
    private scrollService: ScrollService,
    private mediaQueryService: MediaQueryService,
    private toasterService: ToasterService,
    private bookingWidgetService: BookingWidgetService,
    @Inject(PLATFORM_ID) private platform: object
  ) {
    this.offers$ = this.store$.pipe(currentOffers());
    this.filteredOffersOutbound$ = this.store$.pipe(filteredCurrentOffersOutbound());
    this.filteredOfferInbound$ = this.store$.pipe(filteredCurrentOffersInbound());
    this.showKoreaNotification$ = this.offers$.pipe(
      map((offerList: OfferList) => {
        const location = offerList.locations[offerList.finnairSearchParameters?.itinerary[0].departureLocationCode];
        return location && location.countryCode === CountryCode.KOREA;
      }),
      finShare()
    );
    this.currencyCode$ = this.store$.pipe(offersCurrency());
    this.offersStatus$ = this.store$.pipe(offersStatus());
    this.isOneWayOffers$ = this.store$.pipe(isOneWayOffers());
    this.departureDate$ = this.store$.pipe(offersDepartureDate());
    this.returnDate$ = this.store$.pipe(offersReturnDate());
    this.fareFamilies$ = this.store$.pipe(currentFareFamilies());
    this.quotas$ = this.store$.pipe(quotasForBounds());

    this.showOutboundFilters$ = this.offers$.pipe(
      filter(Boolean),
      map((bounds) => Object.keys(bounds.outbounds).length > 3),
      finShare()
    );
    this.showInboundFilters$ = this.offers$.pipe(
      filter(Boolean),
      map((bounds) => Object.keys(bounds.inbounds).length > 3),
      finShare()
    );

    this.selectedOutbound$ = combineLatest([this.filteredOffersOutbound$, this.store$.pipe(selectedOutboundId())]).pipe(
      map(([offers, boundId]) => boundWithDetails(offers, offers.outbounds[boundId])),
      finShare()
    );
    this.selectedInbound$ = combineLatest([
      this.filteredOfferInbound$.pipe(filter((offers) => offers.finnairSearchParameters?.itinerary.length !== 1)),
      this.store$.pipe(selectedInboundId()),
    ]).pipe(
      map(([offers, boundId]: [OfferList, string]) => boundWithDetails(offers, offers.inbounds[boundId])),
      finShare()
    );
    this.selectedOutboundFareFamilyCode$ = this.store$.pipe(selectedOutboundFareFamilyCode());
    this.selectedInboundFareFamilyCode$ = this.store$.pipe(selectedInboundFareFamilyCode());
    this.selectedOutboundFareFamilyBenefits$ = combineLatest([
      this.selectedOutboundFareFamilyCode$,
      this.fareFamilies$,
    ]).pipe(map(([fareFamilyCode, fareFamilies]) => fareFamilies?.[fareFamilyCode]));
    this.selectedInboundFareFamilyBenefits$ = combineLatest([
      this.selectedInboundFareFamilyCode$,
      this.fareFamilies$,
    ]).pipe(map(([fareFamilyCode, fareFamilies]) => fareFamilies?.[fareFamilyCode]));

    this.outbounds$ = this.filteredOffersOutbound$.pipe(
      map((offers) => {
        return mapValues(offers.outbounds, (bound, boundId) => {
          const outboundOffers = offers.offers.filter((o) => o.outboundId === boundId);
          const uniqueBoundFareFamilyCodes = outboundOffers.map((a) => a.outboundFareFamily).filter(unique);
          const fareFamilies = Object.values(offers.fareFamilies).filter((ff) =>
            uniqueBoundFareFamilyCodes.includes(ff.fareFamilyCode)
          );

          return {
            ...bound,
            fareFamilies: getBoundFareFamilies(fareFamilies, outboundOffers, bound, 'outbound'),
          };
        });
      }),
      finShare()
    );
    this.inbounds$ = combineLatest([
      this.filteredOfferInbound$,
      this.selectedOutbound$,
      this.selectedOutboundFareFamilyCode$,
    ]).pipe(
      map(([offers, outbound, selectedOutboundFareFamilyCode]) => {
        return 'inbounds' in offers
          ? mapValues(offers.inbounds, (bound, boundId) => {
              const inboundOffers = offers.offers.filter(
                (o) =>
                  o.inboundId === boundId &&
                  o.outboundId === outbound?.boundId &&
                  o.outboundFareFamily === selectedOutboundFareFamilyCode
              );
              const uniqueBoundFareFamilyCodes = inboundOffers.map((a) => a.inboundFareFamily).filter(unique);
              const fareFamilies = Object.values(offers.fareFamilies).filter((ff) =>
                uniqueBoundFareFamilyCodes.includes(ff.fareFamilyCode)
              );
              return {
                ...bound,
                fareFamilies: getBoundFareFamilies(fareFamilies, inboundOffers, bound, 'inbound'),
              };
            })
          : {};
      }),
      finShare()
    );

    this.boundsSelected$ = this.store$.pipe(boundSelectionsDone());

    // == SELECTABLE OFFERS & BOUNDS ==
    type OffersAndType = { offers: Offer[]; oneWay: boolean };
    const offersAndType$: Observable<OffersAndType> = combineLatest([
      this.offers$.pipe(map((list: OfferList): Offer[] => list.offers)),
      this.isOneWayOffers$,
    ]).pipe(
      map(([offers, oneWay]: [Offer[], boolean]) => ({ offers, oneWay })),
      finShare()
    );
    const outboundId$: Observable<string> = this.selectedOutbound$.pipe(
      map((info) => (info || ({} as BoundInfoWithFareFamilies)).boundId),
      startWith(undefined as string)
    );
    const inboundId$: Observable<string> = this.selectedInbound$.pipe(
      map((info) => (info || ({} as BoundInfoWithFareFamilies)).boundId),
      startWith(undefined as string)
    );
    const byOutboundIdIfPresent =
      (id: string): ((o: Offer) => boolean) =>
      (o) =>
        !id || o.outboundId === id;
    const byInboundIdIfPresent =
      (id: string): ((o: Offer) => boolean) =>
      (o) =>
        !id || o.inboundId === id;
    const boundPrice = (boundId: string, price: string): CheapestBoundPrice => {
      return { boundId, cheapestPrice: price };
    };
    const cheapestPrice = (boundId: string, prices: CheapestBoundPrice[]): CheapestBoundPrice =>
      boundPrice(
        boundId,
        String(Math.min(...prices.filter((p) => p.boundId === boundId).map((p) => Number(p.cheapestPrice))))
      );

    this.selectableOffers$ = combineLatest({
      offersAndType: offersAndType$,
      outbound: outboundId$,
      inbound: inboundId$,
    }).pipe(
      map(({ offersAndType: { offers, oneWay }, outbound, inbound }) =>
        offers.filter(byInboundIdIfPresent(!oneWay && inbound)).filter(byOutboundIdIfPresent(outbound))
      ),
      finShare()
    );

    this.enabledOutboundIds$ = combineLatest([offersAndType$, inboundId$]).pipe(
      map(([{ offers, oneWay }, inbound]: [OffersAndType, string]): string[] =>
        offers
          .filter(byInboundIdIfPresent(!oneWay && inbound))
          .map((o) => o.outboundId)
          .filter(unique)
      ),
      finShare()
    );
    this.enabledInboundIds$ = combineLatest([offersAndType$, outboundId$]).pipe(
      map(([{ offers }, outbound]: [OffersAndType, string]): string[] =>
        offers
          .filter(byOutboundIdIfPresent(outbound))
          .map((o) => o.inboundId)
          .filter(unique)
      ),
      finShare()
    );

    this.cheapestCombinableOutboundPrices$ = combineLatest([offersAndType$, inboundId$]).pipe(
      map(([{ offers, oneWay }, inbound]: [OffersAndType, string]): CheapestBoundPrice[] => {
        const combinableOffers = offers.filter(byInboundIdIfPresent(!oneWay && inbound));
        const outboundIds = combinableOffers.map((offer) => offer.outboundId).filter(unique);
        const prices = combinableOffers.map((offer) => boundPrice(offer.outboundId, offer.outboundPrice));
        return outboundIds.map((outboundId) => cheapestPrice(outboundId, prices));
      }),
      finShare()
    );
    this.cheapestCombinableInboundPrices$ = combineLatest([offersAndType$, outboundId$]).pipe(
      map(([{ offers }, outbound]: [OffersAndType, string]): CheapestBoundPrice[] => {
        const combinableOffers = offers.filter(byOutboundIdIfPresent(outbound));
        const inboundIds = combinableOffers.map((offer) => offer.inboundId).filter(unique);
        const prices = combinableOffers.map((offer) => boundPrice(offer.inboundId, offer.inboundPrice));
        return inboundIds.map((inboundId) => cheapestPrice(inboundId, prices));
      }),
      finShare()
    );

    this.selectedOffer$ = this.store$.pipe(currentOffer());

    this.selectedOfferPoints$ = combineLatest([
      this.filteredOffersOutbound$,
      this.selectedOutbound$,
      this.selectedOutboundFareFamilyCode$,
      this.selectedOffer$.pipe(startWith(undefined)),
    ]).pipe(
      map(([offers, outbound, outBoundFareFamilyCode, o]) => {
        return +(
          o?.totalPointsPrice ||
          offers.offers.find(
            (o) => o.outboundFareFamily === outBoundFareFamilyCode && o.outboundId === outbound?.boundId
          )?.outboundPointsPrice
        );
      }),
      distinctUntilChanged()
    );
    this.selectedOfferPrice$ = combineLatest([
      this.filteredOffersOutbound$,
      this.selectedOutbound$,
      this.selectedOutboundFareFamilyCode$,
      this.selectedOffer$.pipe(startWith(undefined)),
      this.currencyCode$,
    ]).pipe(
      map(([offers, outbound, outBoundFareFamilyCode, o, currencyCode]) => {
        return {
          amount:
            o?.totalPrice ||
            offers.offers.find(
              (o) => o.outboundFareFamily === outBoundFareFamilyCode && o.outboundId === outbound?.boundId
            )?.outboundPrice,
          currencyCode,
        };
      }),
      distinctUntilChanged(),
      startWith(EMPTY_AMOUNT)
    );

    this.selectedOutboundOfferItinerary$ = combineLatest([
      this.filteredOffersOutbound$,
      this.selectedOutbound$,
      this.selectedOutboundFareFamilyCode$.pipe(filter(Boolean)),
    ]).pipe(
      distinctUntilChanged(
        (
          [offersPrev, outboundPrev, outBoundFareFamilyCodePrev],
          [offersNext, outboundNext, outBoundFareFamilyCodeNext]
        ) =>
          offersPrev?.hash === offersNext?.hash &&
          outboundPrev?.boundId === outboundNext?.boundId &&
          outBoundFareFamilyCodePrev === outBoundFareFamilyCodeNext
      ),
      map(([offers, outbound, outBoundFareFamilyCode]) => {
        const outboundFareInformation = offers.offers.find(
          (o) => o.outboundFareFamily === outBoundFareFamilyCode && o.outboundId === outbound?.boundId
        )?.outboundFareInformation;

        return itineraryWithDetails(outboundFareInformation, outbound?.itinerary, offers.locations);
      })
    );

    this.selectedInboundOfferItinerary$ = combineLatest([
      this.filteredOfferInbound$,
      this.selectedInbound$.pipe(filter(Boolean)),
      this.selectedOutbound$,
      this.selectedOutboundFareFamilyCode$.pipe(filter(Boolean)),
    ]).pipe(
      distinctUntilChanged(
        (
          [offersPrev, inboundPrev, outboundPrev, outBoundFareFamilyCodePrev],
          [offersNext, inboundNext, outboundNext, outBoundFareFamilyCodeNext]
        ) =>
          offersPrev?.hash === offersNext?.hash &&
          inboundPrev?.boundId === inboundNext?.boundId &&
          outboundPrev?.boundId === outboundNext?.boundId &&
          outBoundFareFamilyCodePrev === outBoundFareFamilyCodeNext
      ),
      map(([offers, inbound, outbound, outBoundFareFamilyCode]) => {
        const inboundFareInformation = offers.offers.find(
          (o) =>
            o.outboundFareFamily === outBoundFareFamilyCode &&
            o.outboundId === outbound?.boundId &&
            o.inboundId === inbound?.boundId
        )?.inboundFareInformation;

        return itineraryWithDetails(inboundFareInformation, inbound?.itinerary, offers.locations);
      })
    );

    this.locations$ = this.offers$.pipe(
      safeMap((o) => o.locations),
      finShare()
    );
    this.paxAmount$ = this.offers$.pipe(
      map(({ finnairSearchParameters: { adults = 0, c15s, children, infants } }: OfferList) => ({
        adults,
        c15s,
        children,
        infants,
      }))
    );

    this.availableCabinClasses$ = combineLatest([
      this.selectionPhase$.pipe(
        switchMap((phase) => {
          return phase === SelectionPhase.OUTBOUND ? this.outbounds$ : this.inbounds$;
        })
      ),
      this.offers$,
    ]).pipe(
      map(([bounds, offers]) => {
        return [
          Object.keys(bounds).map((bound) => {
            return boundWithDetails(offers, bounds[bound]);
          }),
          offers,
        ];
      }),
      map(([bounds, offers]: [BoundWithLocations[], OfferList]) => {
        return getCabinClassesForAwardBounds(bounds, offers.locations);
      })
    );

    // == SORTING ==
    this.selectedSortBy$ = this.store$.pipe(selectedTicketSortBy());
    type OffersAndSort = [OfferList, SortBy];
    this.outboundIdSortOrder$ = combineLatest([this.filteredOffersOutbound$, this.selectedSortBy$]).pipe(
      map(([offers, sortBy]: OffersAndSort): string[] => offers.sorting.outbounds[offerListSortMap[sortBy]])
    );
    this.inboundIdSortOrder$ = combineLatest([this.filteredOfferInbound$, this.selectedSortBy$]).pipe(
      map(([offers, sortBy]: OffersAndSort): string[] => offers.sorting.inbounds[offerListSortMap[sortBy]])
    );
    this.outboundSorting$ = this.filteredOfferInbound$.pipe(map((offers) => offers.sorting.outbounds));
    this.inboundSorting$ = this.filteredOfferInbound$.pipe(map((offers) => offers.sorting.inbounds));

    // == CONTINUE BUTTON ==
    this.subscription.add(
      this.continueButtonClicks$
        .pipe(withLatestFrom(this.isOneWayOffers$, this.selectionPhase$))
        .subscribe(([, oneWay, currentPhase]: [boolean, boolean, SelectionPhase]) => {
          if (currentPhase === SelectionPhase.OUTBOUND && !oneWay) {
            // Keep other filters, but reset time filters
            this.store$.dispatch(OffersActions.resetArrivalTime());
            this.store$.dispatch(OffersActions.resetDepartureTime());

            this.selectionPhase$.next(SelectionPhase.INBOUND);
          } else {
            this.confirmOffer();
          }
        })
    );
    this.continueEnabled$ = this.selectionPhase$.pipe(
      switchMap((phase: SelectionPhase): Observable<any> => {
        if (phase === SelectionPhase.OUTBOUND) {
          return this.selectedOutbound$.pipe(startWith(undefined as FinnairBoundItem));
        }
        if (phase === SelectionPhase.INBOUND) {
          return this.selectedInbound$.pipe(startWith(undefined as FinnairBoundItem));
        }
        return this.selectedOfferPrice$.pipe(
          map(({ amount }) => amount),
          delay(1), // Prevent ExpressionChangedAfterItHasBeenCheckedError
          startWith(undefined as Amount)
        );
      }),
      map(isPresent),
      distinctUntilChanged()
    );
    this.continueButtonId$ = combineLatest([this.selectionPhase$, this.isOneWayOffers$]).pipe(
      map(([currentPhase, oneWay]: [SelectionPhase, boolean]) => {
        if (currentPhase === SelectionPhase.OUTBOUND && !oneWay) {
          return 'continue-to-inbound-selection';
        } else if ((currentPhase === SelectionPhase.OUTBOUND && oneWay) || currentPhase === SelectionPhase.INBOUND) {
          return 'continue-to-fare-selection';
        } else {
          return 'continue-to-pax-details';
        }
      }),
      distinctUntilChanged()
    );

    // Scroll to top and remove toaster messages on every phase change
    this.subscription.add(
      this.selectionPhase$
        .pipe(
          filter(() => isPlatformBrowser(this.platform)),
          delay(1)
        )
        .subscribe(() => {
          this.scrollService.scrollTop();
          if (this.activeCabinClassNotificationIds.length) {
            this.activeCabinClassNotificationIds.forEach((id) => this.toasterService.removeMessage(id));
            this.activeCabinClassNotificationIds = [];
          }
        })
    );

    this.subscription.add(
      this.openBoundClick$
        .pipe(
          switchMap(({ bound, boundType }) =>
            this.store$.pipe(
              globalBookingPaxAmount(),
              switchMap((paxAmount: PaxAmount) =>
                this.store$.pipe(flightSelectionGtmDataForOffers(bound, boundType, paxAmount)).pipe(take(1))
              )
            )
          )
        )
        .subscribe((gtmFlightSelectionData) => {
          this.gtmService.flightSelection(GtmEvent.FLIGHT_SELECTION_VIEW, gtmFlightSelectionData);
        })
    );

    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.profile$ = this.store$.pipe(profile(), finShare());

    this.tripLocations$ = this.offersStatus$.pipe(
      withLatestFrom(this.store$.pipe(globalBookingFlights())),
      map(([_, locations]) =>
        locations.map(({ destination, origin }) => ({
          destination: destination.cityName,
          origin: origin.cityName,
        }))
      )
    );

    this.subscription.add(
      this.selectionPhase$
        .pipe(
          switchMap((phase) => {
            return (phase === SelectionPhase.OUTBOUND ? this.outbounds$ : this.inbounds$).pipe(
              distinctUntilChanged(compareAsJson),
              map((bounds) => [bounds, phase])
            );
          })
        )
        .subscribe(([bounds, phase]) => {
          this.gtmService.trackElement(
            `${phase === SelectionPhase.OUTBOUND ? 'outbound' : 'inbound'}-flights-shown-award`,
            GaContext.FLIGHT_SELECTION,
            ElementTypes.ACCORDION,
            Object.entries(bounds).length.toString(),
            ElementActions.VIEW
          );
        })
    );
  }

  ngOnInit(): void {
    this.subscription.add(
      merge(this.selectedOutboundOfferItinerary$, this.selectedInboundOfferItinerary$)
        .pipe(
          filter((itinerary) => !containsOnlyOneCabin(itinerary)),
          switchMap((itinerary) => {
            const businessFaultySegments =
              !containsOnlyOneCabin(itinerary, GlobalBookingTravelClass.BUSINESS) &&
              itinerary.filter(({ cabinClass }) => cabinClass !== GlobalBookingTravelClass.BUSINESS);

            const ecoPremiumFaultySegments =
              !containsOnlyOneCabin(itinerary, GlobalBookingTravelClass.ECOPREMIUM) &&
              itinerary.filter(({ cabinClass }) => cabinClass !== GlobalBookingTravelClass.ECOPREMIUM);

            const faultySegments =
              isPresent(businessFaultySegments) && businessFaultySegments.length === 1
                ? businessFaultySegments
                : isPresent(ecoPremiumFaultySegments) && ecoPremiumFaultySegments.length === 1
                  ? ecoPremiumFaultySegments
                  : undefined;

            const params: LocationNames = {};
            if (faultySegments) {
              // find the origin and destination for the faulty segment.
              const [fs] = faultySegments;
              const [segment] = itinerary.filter((seg) => seg.id === fs.id);

              params.origin = segment.departure.cityName;
              params.destination = segment.arrival.cityName;
            }

            return combineLatest([
              this.selectedOutbound$,
              this.selectedInbound$,
              this.languageService.translate(
                businessFaultySegments.length === 1
                  ? 'ticketSelection.differentCabinClassWarningNew'
                  : ecoPremiumFaultySegments.length === 1
                    ? 'ticketSelection.differentCabinClassPremiumEconomyWarningNew'
                    : 'ticketSelection.differentCabinClassComplexWarning',
                params
              ),
              this.languageService.translate('ticketSelection.differentCabinClassWarningLink.linkText'),
            ]);
          }),
          distinctUntilChanged(
            (
              [selectedOutboundPrev, selectedInboundPrev, notificationTextPrev, linkTextPrev],
              [selectedOutboundNext, selectedInboundNext, notificationTextNext, linkTextNext]
            ) => {
              return (
                selectedOutboundPrev?.boundId === selectedOutboundNext?.boundId &&
                selectedInboundPrev?.boundId === selectedInboundNext?.boundId &&
                notificationTextPrev === notificationTextNext &&
                linkTextPrev === linkTextNext
              );
            }
          ),
          finShare()
        )
        .subscribe(([selectedOutbound, selectedInbound, notificationText, linkText]) => {
          const id = uuid();
          this.activeCabinClassNotificationIds.push(id);

          this.toasterService.addMessageToQueue({
            id,
            content: notificationText,
            theme: NotificationTheme.WARNING,
            target: {
              linkText: linkText,
              callback: () => {
                this.showDetailsModal(selectedOutbound, selectedInbound, this.selectionPhase$.getValue());
                this.activeCabinClassNotificationIds = this.activeCabinClassNotificationIds.filter(
                  (storedId) => storedId !== id
                );
              },
            },
            keepMessageAfterNavigation: false,
          });
        })
    );

    this.subscription.add(
      this.selectionPhase$.pipe(distinctUntilChanged()).subscribe((selectionPhase) => {
        this.selectionPhaseChange.emit(selectionPhase);
      })
    );

    this.subscription.add(
      this.goToOutboundPhase$?.subscribe(() => {
        const selectionPhase = this.selectionPhase$.getValue();
        if (selectionPhase === SelectionPhase.INBOUND) {
          this.selectionPhase$.next(SelectionPhase.OUTBOUND);
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  backToDateSelection(): void {
    this.bookingService.setStep({ step: BOOKING_STEPS.ENTRY });
  }

  goToSelectionPhase(phase: SelectionPhase): void {
    if (this.selectionPhase$.getValue() === phase) {
      return;
    }
    this.selectionPhase$.next(phase);

    this.store$.dispatch(OffersActions.resetArrivalTime());
    this.store$.dispatch(OffersActions.resetDepartureTime());
  }

  selectOutbound(bound: BoundInfoWithFareFamilies): void {
    this.store$.dispatch(OffersActions.setOutboundId({ outboundId: bound.boundId }));
  }

  selectInbound(bound: BoundInfoWithFareFamilies): void {
    this.store$.dispatch(OffersActions.setInboundId({ inboundId: bound.boundId }));
  }

  selectOutboundFareFamily(outboundFareFamily: string): void {
    this.store$.dispatch(OffersActions.setOutboundFareFamily({ outboundFareFamily }));

    const offer: Offer = snapshot(this.store$.pipe(currentOffer()));
    if (!offer) {
      this.store$.dispatch(OffersActions.setInboundId({ inboundId: undefined }));
      this.store$.dispatch(OffersActions.setInboundFareFamily({ inboundFareFamily: undefined }));
    }
  }

  selectInboundFareFamily(inboundFareFamily: string): void {
    this.store$.dispatch(OffersActions.setInboundFareFamily({ inboundFareFamily }));
  }

  selectSortBy(sortBy: SortBy): void {
    this.store$.dispatch(OffersActions.setSortBy({ sortBy }));

    this.selectedSortCriteria$ = of(sortBy);
    this.onShowSpinner();
  }

  onShowSpinner(): void {
    this.isLoading$.next(true);

    setTimeout(() => {
      this.isLoading$.next(false);
    }, FAKE_LOAD_TIME);
  }

  onContinueClick(event: Event): void {
    stopPropagation(event);
    this.bookingWidgetService.setGlobalBookingSelectionToOriginal();
    this.continueButtonClicks$.next(true);
  }

  confirmOffer(): void {
    const offer: Offer = snapshot(this.store$.pipe(currentOffer()));
    this.triggerCreateCart(offer);
    const zeroBaggage: boolean = snapshot(this.store$.pipe(hasZeroBaggageOnEitherFlight(offer)));

    if (zeroBaggage) {
      this.continueModal = {
        open: true,
        data: {
          zeroBaggage,
        },
      };
    } else {
      this.continueToNextStep();
    }
  }

  continueToNextStep(): void {
    this.bookingService.setStep({ step: BOOKING_STEPS.PAXDETAILS });
  }

  closeModals(): void {
    this.itineraryModal = { open: false, data: {} };
    this.continueModal = { open: false, data: null };
    this.benefitsModal = { open: false, data: null };
  }

  showDetailsModal(
    outbound: BoundInfoWithFareFamilies,
    inbound: BoundInfoWithFareFamilies,
    selectionPhase: SelectionPhase
  ): void {
    this.itineraryModal = {
      open: true,
      data: {
        outbound: this.getBoundWithItineraryDetails(outbound, snapshot(this.selectedOutboundOfferItinerary$)),
        inbound: this.getBoundWithItineraryDetails(inbound, snapshot(this.selectedInboundOfferItinerary$)),
      },
    };

    this.gtmService.trackElement(
      `${selectionPhase.toLowerCase()}::view-flight-details`,
      GaContext.FLIGHT_SELECTION,
      ElementTypes.LINK,
      undefined,
      ElementActions.CLICK
    );
  }

  handleBoundOpen(bound: BoundInfoWithFareFamilies, boundType: BoundType): void {
    this.openBoundClick$.next({ bound, boundType });
  }

  private triggerCreateCart(offer: Offer): void {
    const offersHashValue: string = snapshot(this.store$.pipe(offersHash()));
    const locale = this.languageService.localeValue;
    this.bookingQueueService.queueCartCreate(offer.offerId, locale, offersHashValue);
  }

  private getBoundWithItineraryDetails(
    bound: BoundInfoWithFareFamilies | null,
    selectedBoundWithItineraryFlightDetails: ItineraryDetails[] | null
  ): BoundInfoWithFareFamilies | null {
    if (!isPresent(bound)) {
      return null;
    }

    const hasBoundAndDetails = isPresent(bound) && isPresent(selectedBoundWithItineraryFlightDetails);

    return hasBoundAndDetails
      ? {
          ...bound,
          itinerary: bound.itinerary.map((flight) => {
            const fareInformation = selectedBoundWithItineraryFlightDetails.find(
              (fareInfo: ItineraryDetails) => fareInfo.id === (flight as ItineraryItemFlight)?.id
            );

            return {
              ...flight,
              ...(isPresent(fareInformation) && fareInformation),
            };
          }),
        }
      : bound;
  }
}
